import { useMemo } from "react";
import { Link, Outlet, useParams } from "react-router";

import { useAuth } from "@/providers";
import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import {
  auditFacts,
  type EntityHeaderFact,
  EntityHeader,
  EntityName,
  EntityTabs,
  type EntityTabItem,
  useActiveEntityTab,
} from "@/shared/components/EntityPage";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useNotification } from "@/shared/components/notifications/useNotification";
import type { Device as DeviceRecord } from "@/shared/domain/devices";
import { useDevice } from "@/shared/domain/devices/hooks/useDevice";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";
import { useDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  Info,
  KeyRound,
  LayoutDashboard,
  SlidersHorizontal,
  EntityIcon,
} from "@/shared/foundation/icons";
import { Badge, Banner } from "@/shared/foundation/primitives";
import { DeviceTab, Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import styles from "./Device.module.css";
import type { DeviceContext } from "./useDeviceContext";

/**
 * Parent layout for the `/devices/:deviceId` route tree. Renders the page
 * chrome (breadcrumb, header block and tab bar) and delegates the active
 * tab's body to `<Outlet>`. Publishes `{ deviceId, org }` as outlet context so
 * descendant tab routes can read them via `useDeviceContext` instead of
 * re-deriving from `useParams` / `useAuth`.
 *
 * Renders an error in place of the chrome when the URL is missing
 * `:deviceId`; downstream errors thrown inside a tab are caught by the
 * shared `<ErrorBoundary>` wrapping the outlet.
 */
export function Device() {
  const { deviceId } = useParams();
  const { currentOrganization: org } = useAuth();

  // Memoize so the object handed to <Outlet context={...}> keeps a stable
  // reference across renders whose inputs haven't changed. Without this,
  // every render of <Device /> would publish a fresh context object and
  // invalidate referential-equality checks (memo deps, effect deps) in
  // descendant tabs that consume the bundle through useDeviceContext.
  const outletContext = useMemo<DeviceContext | null>(() => {
    if (!deviceId) {
      return null;
    }
    return {
      deviceId,
      org,
    };
  }, [deviceId, org]);

  // Publish the device to AI Chat so the agent can resolve "this device"
  // without the user pasting its id. Registered before the `!deviceId` guard
  // below so the hook runs on every render; an undefined id contributes
  // nothing. No `orgId`: a device is only loadable from within its owning
  // org, so there is never an org switch to offer (see `ViewingContextMap`).
  useRegisterViewingContext("device", { deviceId });

  // Guard: a route hit without `:deviceId` can't render the device chrome
  // or hydrate child tabs; surface the misconfiguration inline rather than
  // letting downstream hooks fire with an undefined id.
  if (!deviceId) {
    return (
      <PageLayout title="Roboto - Device">
        <Banner variant="error">Missing device id in path</Banner>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`Roboto - Device - ${deviceId}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Devices}>Devices</Link>
          <ExpandableCopyText text={deviceId} trimLength={36} />
        </PageBreadcrumbs>
      }
    >
      <ErrorBoundary
        fallback={(props) => {
          if (props.error instanceof Error) {
            return <Banner variant="error">{props.error.message}</Banner>;
          }

          return (
            <Banner variant="error">
              Something went wrong. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Banner>
          );
        }}
      >
        <div className={styles.root}>
          <DeviceChrome deviceId={deviceId} orgId={org?.org_id} />
          <div className={styles.tabPanel}>
            <Outlet context={outletContext} />
          </div>
        </div>
      </ErrorBoundary>
    </PageLayout>
  );
}

/**
 * Header and tab bar for `/devices/:deviceId`. A device has no name or
 * description, so its ID is the title and the facts leave the ID out. The
 * type badge, tags and facts appear once `useDevice` resolves
 * (TanStack Query caches it, so the Details tab's own call is a cache hit);
 * the tab bar renders regardless, since it reads its active value from the
 * URL.
 */
function DeviceChrome({
  deviceId,
  orgId,
}: {
  deviceId: string;
  orgId: string | undefined;
}) {
  const deviceQuery = useDevice(deviceId, orgId);
  const device = deviceQuery.data ?? null;
  const notify = useNotification();
  const { mutateAsync: updateDevice } = useUpdateDevice();
  const updateTags = (metadata_changeset: {
    put_tags?: string[];
    remove_tags?: string[];
  }) =>
    updateDevice({
      deviceId,
      resourceOwnerId: orgId ?? "",
      updates: { metadata_changeset },
    }).catch(() => notify("Couldn't save the change", "error"));
  // Each flag also gates its tab's route in DevicesRoutes.tsx, so the strip
  // shows only tabs that can be reached.
  const overviewTabEnabled = useCheckFeatureFlag("release-device-timeline");
  const configurationsTabEnabled = useCheckFeatureFlag("release-asset-store");
  const tabs: EntityTabItem<DeviceTab>[] = [
    ...(overviewTabEnabled
      ? [
          {
            value: DeviceTab.Overview,
            label: "Overview",
            icon: <LayoutDashboard />,
          },
        ]
      : []),
    {
      value: DeviceTab.Sessions,
      label: "Sessions",
      icon: <EntityIcon type="session" />,
    },
    {
      value: DeviceTab.Datasets,
      label: "Datasets",
      icon: <EntityIcon type="dataset" />,
    },
    {
      value: DeviceTab.Metrics,
      label: "Metrics",
      icon: <EntityIcon type="metric" />,
    },
    ...(configurationsTabEnabled
      ? [
          {
            value: DeviceTab.Configurations,
            label: "Configurations",
            icon: <SlidersHorizontal />,
          },
        ]
      : []),
    "divider",
    { value: DeviceTab.Tokens, label: "Tokens", icon: <KeyRound /> },
    { value: DeviceTab.Details, label: "Details", icon: <Info /> },
  ];
  // Datasets is the device's landing tab, whichever tabs come before it.
  const activeTab = useActiveEntityTab(tabs, DeviceTab.Datasets);

  return (
    <div>
      <EntityHeader
        type="device"
        loading={deviceQuery.isLoading}
        name={<EntityName>{deviceId}</EntityName>}
        id={device?.universal_device_id ?? undefined}
        badges={device && <DeviceTypeChip device={device} />}
        tags={
          device
            ? {
                value: device.tags,
                canAdd: true,
                onAdd: (tag) => void updateTags({ put_tags: [tag] }),
              }
            : undefined
        }
        meta={device ? deviceFacts(device) : []}
      />
      {/* A tab opens on its own defaults: one tab's query string (the
          Overview's window and filters) means nothing on another. */}
      <EntityTabs
        tabs={tabs}
        activeTab={activeTab}
        keepSearch={false}
        aria-label="device tabs"
      />
    </div>
  );
}

/** Names the device's type and links to it. Hidden until the type is known. */
function DeviceTypeChip({ device }: { device: DeviceRecord }) {
  const assetStoreEnabled = useCheckFeatureFlag(
    "release-asset-store",
    device.org_id,
  );
  const deviceTypeId = assetStoreEnabled ? device.device_type_id : undefined;
  const deviceType = useDeviceType(deviceTypeId ?? undefined, device.org_id);
  if (!deviceTypeId) {
    return null;
  }
  return (
    <Link
      to={Goto.createDeviceTypeRoute(deviceTypeId)}
      className={styles.typeChip}
    >
      <Badge variant="label">{deviceType.data?.name ?? deviceTypeId}</Badge>
    </Link>
  );
}

/**
 * Who created it and when. Its Roboto ID (dv_…),
 * when it has one, follows as the header's ID; its name is the title.
 */
function deviceFacts(device: DeviceRecord): EntityHeaderFact[] {
  return [
    ...auditFacts({ created: device.created, createdBy: device.created_by }),
  ];
}
